// v-proofread カスタムディレクティブ
//
// 使い方:
//   <el-input type="textarea" v-model="formatData.reason" v-proofread />
//
// 動作:
//   bind フックで el-input 内部の <textarea> を取得し、
//   同じ wrapper にオーバーレイ <div> を absolute 重ねする。
//   input イベントを debounce (300ms) で拾って Worker に投げ、
//   結果を overlay に反映する。ユーザーが赤波線をクリックすると
//   修正候補ツールチップが表示される。
//
// element-ui v2 では、el-input の内部構造が mount 直後に取れないことがあるので、
// requestAnimationFrame で少し待って再取得する。
//
// Vue 2 の Directive フック:
//   bind       — 初回 attach
//   inserted   — DOM 挿入後
//   componentUpdated — 親コンポーネントの再描画後
//   unbind     — 掃除

import { lintText, debounce } from '../manager'
import {
  createOverlay,
  renderOverlay,
  syncScroll,
  destroyOverlay
} from '../overlay'
import { showTooltip, hideTooltip } from '../tooltip'
import { attachGhostText } from '../phase2/ghost-text'
import { attachBlueWave } from '../phase2/blue-wave'

const DEBOUNCE_MS = 300
const state = new WeakMap()

function findTextarea(el) {
  if (!el) return null
  if (el.tagName === 'TEXTAREA') return el
  return el.querySelector('textarea')
}

// ディレクティブ binding value を正規化する
// `v-proofread` → {}, `v-proofread="{ phase2: true }"` → { phase2: true }
function normalizeOptions(value) {
  if (!value || typeof value !== 'object') return {}
  return value
}

// Vue インスタンス（vnode.context）から varuna API 用の fetcher を組み立てる。
// uuid / axios / endpoints を直接参照する。Vue 外モジュールでも疎結合に動かすため。
function buildGhostFetcher(vueContext) {
  if (!vueContext) return null
  const $axios = vueContext.$axios
  const $varuna = vueContext.$varuna
  const $firebase = vueContext.$firebase
  if (
    !$axios ||
    !$varuna ||
    !$varuna.endPoints ||
    !$varuna.endPoints.proofreadGhost
  ) {
    return null
  }
  const endpoint = $varuna.endPoints.proofreadGhost
  return function fetcher({
    textBeforeCursor,
    textAfterCursor,
    context,
    signal
  }) {
    const uid = ($firebase && $firebase.user && $firebase.user.uid) || null
    if (!uid) {
      // 未ログインは課金ゲート対象外として、何も提案しない
      return Promise.resolve('')
    }
    const config = { timeout: 15000 }

    // axios 0.18 は AbortController.signal 未対応 → CancelToken にブリッジ
    let cancelSource = null
    if (signal && $axios.CancelToken) {
      cancelSource = $axios.CancelToken.source()
      config.cancelToken = cancelSource.token
      if (signal.aborted) {
        cancelSource.cancel('aborted before send')
      } else {
        signal.addEventListener('abort', () => {
          if (cancelSource) cancelSource.cancel('aborted')
        })
      }
    }

    return $axios
      .post(
        endpoint,
        {
          uuid: uid,
          text_before_cursor: textBeforeCursor,
          text_after_cursor: textAfterCursor,
          context
        },
        config
      )
      .then((res) => {
        if (res && res.data && res.data.success) {
          return res.data.continuation || ''
        }
        return ''
      })
      .catch((err) => {
        // 自分の abort はそのまま再スロー（ghost-text.js 側で signal.aborted で識別）
        if ($axios.isCancel && $axios.isCancel(err)) {
          throw err
        }
        if (signal && signal.aborted) {
          throw err
        }
        // 403 (非会員) / 429 (上限) などはサイレントに無視（ghost を出さない）
        return ''
      })
  }
}

// 青波線（言い換え提案）用 fetcher を組み立てる。
function buildBlueWaveFetcher(vueContext) {
  if (!vueContext) return null
  const $axios = vueContext.$axios
  const $varuna = vueContext.$varuna
  const $firebase = vueContext.$firebase
  if (
    !$axios ||
    !$varuna ||
    !$varuna.endPoints ||
    !$varuna.endPoints.proofreadRewrite
  ) {
    return null
  }
  const endpoint = $varuna.endPoints.proofreadRewrite
  return function fetcher({ text, context, signal }) {
    const uid = ($firebase && $firebase.user && $firebase.user.uid) || null
    if (!uid) {
      return Promise.resolve([])
    }
    const config = { timeout: 30000 }
    let cancelSource = null
    if (signal && $axios.CancelToken) {
      cancelSource = $axios.CancelToken.source()
      config.cancelToken = cancelSource.token
      if (signal.aborted) {
        cancelSource.cancel('aborted before send')
      } else {
        signal.addEventListener('abort', () => {
          if (cancelSource) cancelSource.cancel('aborted')
        })
      }
    }
    return $axios
      .post(
        endpoint,
        {
          uuid: uid,
          text,
          context
        },
        config
      )
      .then((res) => {
        if (
          res &&
          res.data &&
          res.data.success &&
          Array.isArray(res.data.suggestions)
        ) {
          return res.data.suggestions
        }
        return []
      })
      .catch((err) => {
        if ($axios.isCancel && $axios.isCancel(err)) {
          throw err
        }
        if (signal && signal.aborted) {
          throw err
        }
        // 403/429 等はサイレント
        return []
      })
  }
}

// 採用 / 破棄イベントを記録する reporter を組み立てる（fire-and-forget）。
// 既存 fetcher と同じく vueContext から axios / endpoint / uid を引く。
// feature は呼び出し側で bind し、ここでは report(feature, action, extra) を返す。
function buildEventReporter(vueContext) {
  if (!vueContext) return null
  const $axios = vueContext.$axios
  const $varuna = vueContext.$varuna
  const $firebase = vueContext.$firebase
  if (
    !$axios ||
    !$varuna ||
    !$varuna.endPoints ||
    !$varuna.endPoints.proofreadEvent
  ) {
    return null
  }
  const endpoint = $varuna.endPoints.proofreadEvent
  return function report(feature, action, extra) {
    const uid = ($firebase && $firebase.user && $firebase.user.uid) || null
    if (!uid) return // 未ログインは記録しない（会員機能なので通常ありえない）
    const payload = { uuid: uid, feature, action }
    if (extra && typeof extra.candidateIndex === 'number') {
      payload.candidate_index = extra.candidateIndex
    }
    if (extra && extra.payload && typeof extra.payload === 'object') {
      payload.payload = extra.payload
    }
    // fire-and-forget: 記録の成否は採用/破棄の本処理・UX に影響させない
    try {
      $axios.post(endpoint, payload, { timeout: 5000 }).catch(() => {})
    } catch (e) {
      /* noop */
    }
  }
}

function setup(rootEl, options, vueContext) {
  if (state.has(rootEl)) return // 二重 setup 防止
  const textarea = findTextarea(rootEl)
  if (!textarea) return false

  const wrapper = textarea.parentNode
  if (!wrapper) return false

  const overlay = createOverlay(wrapper)

  // 最新の lint 結果を保持して、カード切替（v-show）で textarea が
  // 非表示→表示に変わったときに正しいサイズで再描画できるようにする
  const ctx = {
    textarea,
    overlay,
    lastMessages: [],
    dismissedKeys: new Set(), // 「このままにする」で無視した指摘のキー
    onInput: null,
    onScroll: null,
    onBlur: null,
    debouncedLint: null,
    resizeObserver: null,
    runLint: null
  }

  // dismiss キー生成: ruleId + 対象テキストで一意に識別
  function makeDismissKey(msg, range, text) {
    const start = range ? range.start : 0
    const end = range ? range.end : 0
    const flaggedText = text.slice(start, end)
    return (msg.ruleId || '') + ':' + flaggedText
  }

  // dismissed をフィルタした messages を返す
  function filterDismissed(messages, text) {
    if (ctx.dismissedKeys.size === 0) return messages
    return messages.filter((msg) => {
      let start, end
      if (Array.isArray(msg.range)) {
        ;[start, end] = msg.range
      } else if (msg.fix && Array.isArray(msg.fix.range)) {
        ;[start, end] = msg.fix.range
      } else {
        start = typeof msg.index === 'number' ? msg.index : 0
        end = Math.min(start + 2, text.length)
      }
      const key = (msg.ruleId || '') + ':' + text.slice(start, end)
      return !ctx.dismissedKeys.has(key)
    })
  }

  const onDismiss = (msg, range) => {
    const text = textarea.value || ''
    const key = makeDismissKey(msg, range, text)
    ctx.dismissedKeys.add(key)
    rerender()
  }

  const onMarkClick = (msg, anchor, range) => {
    showTooltip(msg, anchor, textarea, range, { onDismiss })
  }

  const rerender = () => {
    const text = textarea.value || ''
    const filtered = filterDismissed(ctx.lastMessages, text)
    renderOverlay(overlay, textarea, text, filtered, onMarkClick)
  }

  const runLint = async () => {
    const text = textarea.value || ''
    try {
      const messages = await lintText(text)
      // 途中で unbind されている可能性があるのでガード
      if (!state.has(rootEl)) return
      ctx.lastMessages = messages
      rerender()
    } catch (err) {
      console.warn('[v-proofread] lint failed', err)
    }
  }

  const debouncedLint = debounce(runLint, DEBOUNCE_MS)

  const onInput = () => {
    hideTooltip()
    debouncedLint()
  }
  const onScroll = () => syncScroll(overlay, textarea)
  const onBlur = () => {
    // blur 時に即座に lint（debounce を待たない）して保存前チェックを確実にする
    debouncedLint.cancel()
    runLint()
  }

  textarea.addEventListener('input', onInput)
  textarea.addEventListener('scroll', onScroll)
  textarea.addEventListener('blur', onBlur)

  // ResizeObserver で textarea サイズ変化（autosize / v-show切替）に追従
  let resizeObserver = null
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      // 最新の lint 結果を使って再描画（サイズが変わったら overlay を合わせ直す）
      rerender()
      // テキストも変わっている可能性があるので再 lint
      debouncedLint()
    })
    resizeObserver.observe(textarea)
  }

  ctx.onInput = onInput
  ctx.onScroll = onScroll
  ctx.onBlur = onBlur
  ctx.debouncedLint = debouncedLint
  ctx.resizeObserver = resizeObserver
  ctx.runLint = runLint
  ctx.rerender = rerender

  // Phase 2: ghost text + 青波線フック（opt-in）
  ctx.ghostHandle = null
  ctx.blueHandle = null
  if (options && options.phase2) {
    const phase2Context = options.context || '文章'
    const reportEvent = buildEventReporter(vueContext)
    try {
      const ghostFetcher = buildGhostFetcher(vueContext)
      ctx.ghostHandle = attachGhostText(textarea, {
        fetcher: ghostFetcher,
        context: phase2Context,
        onEvent: reportEvent
          ? (action, extra) => reportEvent('ghost', action, extra)
          : null
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[v-proofread] ghost text attach failed', err)
    }
    try {
      const blueFetcher = buildBlueWaveFetcher(vueContext)
      ctx.blueHandle = attachBlueWave(textarea, {
        fetcher: blueFetcher,
        context: phase2Context,
        onEvent: reportEvent
          ? (action, extra) => reportEvent('rewrite', action, extra)
          : null
      })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[v-proofread] blue wave attach failed', err)
    }
  }

  state.set(rootEl, ctx)

  // 初回: textarea が表示中なら即 lint、非表示なら componentUpdated で拾う
  if (textarea.offsetWidth > 0) {
    runLint()
  }
  return true
}

function teardown(rootEl) {
  const s = state.get(rootEl)
  if (!s) return
  try {
    s.textarea.removeEventListener('input', s.onInput)
    s.textarea.removeEventListener('scroll', s.onScroll)
    s.textarea.removeEventListener('blur', s.onBlur)
  } catch (e) {
    /* noop */
  }
  if (s.debouncedLint && s.debouncedLint.cancel) s.debouncedLint.cancel()
  if (s.resizeObserver) {
    try {
      s.resizeObserver.disconnect()
    } catch (e) {
      /* noop */
    }
  }
  if (s.ghostHandle && typeof s.ghostHandle.destroy === 'function') {
    try {
      s.ghostHandle.destroy()
    } catch (e) {
      /* noop */
    }
  }
  if (s.blueHandle && typeof s.blueHandle.destroy === 'function') {
    try {
      s.blueHandle.destroy()
    } catch (e) {
      /* noop */
    }
  }
  destroyOverlay(s.overlay)
  state.delete(rootEl)
}

const proofreadDirective = {
  bind(el, binding, vnode) {
    const options = normalizeOptions(binding && binding.value)
    const vueContext = vnode && vnode.context
    // el-input の mount タイミングの関係で、bind 時点では内部 textarea がまだない場合あり
    const ok = setup(el, options, vueContext)
    if (!ok) {
      // 次フレームで再試行
      requestAnimationFrame(() => {
        if (!state.has(el)) setup(el, options, vueContext)
      })
    }
  },
  inserted(el, binding, vnode) {
    const options = normalizeOptions(binding && binding.value)
    const vueContext = vnode && vnode.context
    if (!state.has(el)) setup(el, options, vueContext)
  },
  componentUpdated(el) {
    const s = state.get(el)
    if (!s) return
    // v-show でカードが切り替わり textarea が表示された場合、
    // overlay のサイズが 0×0 のままなので最新 lint 結果で再描画する
    if (s.textarea.offsetWidth > 0 && s.rerender) {
      s.rerender()
    }
    // 外部（AI生成機能等）から v-model が書き換わった場合に備えて再 lint
    if (s.debouncedLint) s.debouncedLint()
  },
  unbind(el) {
    teardown(el)
  }
}

export default proofreadDirective
